import { createContext, useContext, type ReactNode } from 'react';
import type { HierarchicalOption, RecommendedKeys } from '../../../common/HierarchicalSelect';
import type { RecommendationFieldEntry } from '../../hooks/useRecommendations';
import type { PromptModel } from '../../hooks/usePromptModels';
import type { GenerationModel } from '../../../../features/generation-models/api';
import type {
  BehaviorToggleOption,
  CastingActor,
  CastingSelectionState,
  ScenarioInputTab,
  SelectedCasting,
  SelectedSeedancePresetActor,
  SeedancePresetActorItem,
  VideoGenerationMode,
} from '../../state/useGenerationState';
import type { VideoSettings } from '../VideoGenerationSettings';
import type { Mode } from '../types';
import type { ScenarioFormState } from './useScenarioFormState';
import type { ScenarioModelDef, ScenarioModelOption } from './useScenarioModelOptions';
import type { ImportedScriptState } from '../scriptImport';
import type { SourceVideoState } from '../sourceVideo';

export interface CastingModelsQuery {
  data: PromptModel[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * SetupPanel 配下 (ProductSection / CastingSection / ScenarioSettingsSection /
 * FirstFrameSettingsSection / VideoSettingsSection / PromptModePanel) が参照する
 * 「シナリオ設定フォームの値・setter・選択肢・派生表示」の供給口。
 *
 * 判断基準:
 * - フォーム state とその setter / 変更ハンドラ、選択肢ソース、フォーム値から導かれる
 *   要約・警告・見積 → この Context
 * - 親のオーケストレーション (送信・生成実行・モーダル開閉・フック オブジェクト
 *   (product / accordions / mutation) ・プロンプトモードの出力周り) → SetupPanel の props
 */
export interface ScenarioSetupContextValue extends ScenarioFormState {
  // 入力モード
  mode: Mode;
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
  scenarioInputTab: ScenarioInputTab;
  setScenarioInputTab: (next: ScenarioInputTab) => void;

  // 動画生成モード
  videoGenerationMode: VideoGenerationMode;
  setVideoGenerationMode: (next: VideoGenerationMode) => void;
  isReferenceImageMode: boolean;

  // 生成トグル (useGenerationState 由来)
  avoidFaces: boolean;
  setAvoidFaces: React.Dispatch<React.SetStateAction<boolean>>;
  illustrationStoryboard: boolean;
  setIllustrationStoryboard: React.Dispatch<React.SetStateAction<boolean>>;

  // 映像スタイル
  visualStyle: string;
  setVisualStyle: React.Dispatch<React.SetStateAction<string>>;
  /** 映像スタイルの DB ツリー由来選択肢 (属性ツリー移送・フェーズ2)。 */
  visualStyleHierarchicalOptions: HierarchicalOption[];
  visualStyleTreeEnabled: boolean;
  /** 選択中映像スタイルが stylized か (DB の visual_family 由来)。AI俳優/絵コンテ無効化に使う。 */
  visualStyleIsStylized: boolean;
  /** 映像スタイルフィールドの表示位置。primary=主表示 / advanced=詳細設定エキスパンド内。 */
  visualStyleFieldPlacement: 'primary' | 'advanced';
  onVisualStyleChange: (v: string) => void;

  // トーン / 想定尺の選択肢と確定ハンドラ (推奨採用ログ込み)
  toneHierarchicalOptions: HierarchicalOption[];
  onToneChange: (v: string) => void;
  onDurationChange: (v: string) => void;

  // 歌モノ (トーン option の metadata.bgm_mode==='lyrics') 選択時の想定尺下限 30 秒
  // (docs/features/scenario-song-lyrics-bgm-plan.md §5.1.4)。
  /** 選択中トーンが歌モノか。true の間、想定尺の 30 秒未満 option を disable する。 */
  isLyricsTone: boolean;
  /** 全編モーション (「モーショングラフィックス」の whole・移行前は映像スタイル option metadata.production_engine === 'motion'・
   *  §10.5 / §10.14) か。true のとき参考画像 (フレーム / 絵コンテ) の設定を出さず、歌モノのトーンを選べなくする。 */
  isMotionEngine?: boolean;

  // 商品AIプロファイル推奨 (計画書§5.4/§5.5/§8 P4)
  toneRecommendedKeys?: RecommendedKeys;
  visualStyleRecommendedKeys?: RecommendedKeys;
  /** 「モーショングラフィックス」の推奨 (whole の reco_tags が映像スタイルの最高スコアを上回るとき・§10.14) */
  motionGraphicsRecommendedKeys?: RecommendedKeys;
  recommendedDuration?: RecommendationFieldEntry | null;

  // 登場人物数 / ナレ・セリフ・テロップのツリー状態 (フェーズ6)
  characterCountTreeEnabled: boolean;
  narrationTreeOptions: BehaviorToggleOption[];
  narrationTreeEnabled: boolean;
  narrationCategoryEnabled: boolean;
  dialogueTreeOptions: BehaviorToggleOption[];
  dialogueTreeEnabled: boolean;
  dialogueCategoryEnabled: boolean;
  telopTreeOptions: BehaviorToggleOption[];
  telopTreeEnabled: boolean;
  telopCategoryEnabled: boolean;

  // 要約表示
  scenarioSettingsSummary: string[];
  videoSettingsSummaryScenario: string;
  videoSettingsSummaryPrompt: string;

  // モデル選択
  scenarioModelOptions: ScenarioModelOption[];
  /** カタログ全モデルの定義 (rate 込み)。ポータルドロップダウンのグルーピング表示に使う */
  scenarioModelDefs: ScenarioModelDef[];
  /** ランダム枠の 1 本あたり消費レンジ表記 ("2~30" 等)。有効モデルなしなら undefined */
  randomRateBadge: string | undefined;
  currentScenarioModelDef: ScenarioModelDef | null;
  selectedScenarioModelRate: number | null;
  /** 「ランダム」選択時の総消費レンジ文字列 (例 "24〜96")。非ランダム時は未使用。 */
  randomCreditText: string | null;
  /** 生成ボタン/見積に出す消費目安。ランダムはレンジ文字列、それ以外は レート×本数。 */
  scenarioCreditCost: number | string;
  /** プロンプト入力モードの推定消費 (動画1本)。 */
  estimatedVideoCredits: number;
  /** 選択中の動画モデルが「合算課金モデル」か (DB metadata.aggregates_reference_video_seconds 由来。
   *  MiniMax H3 が該当。計画書 §4.1)。true のとき参照動画の尺が estimatedVideoCredits に加算されている。 */
  aggregatesReferenceVideoSeconds: boolean;
  /** 課金対象の参照動画「合計」尺 (秒)。PromptModePanel の最長尺 (連動用) とは別物。
   *  aggregatesReferenceVideoSeconds が true のときのみ見積りに算入される。
   *  2026-08-07 ユーザー決定: バックエンドは非整数秒の素材を拒否せず Math.ceil で
   *  切り上げて課金するため、ここも素材ごとに ceil してから合算した値 (= バックエンドの
   *  課金値と一致する)。 */
  referenceVideoSecondsTotal: number;
  /** 選択中の参照動画に非整数秒 (= 切り上げが発生する) の尺を持つものがあるか
   *  (合算課金モデルのみ判定)。バックエンドは拒否せず切り上げて課金するため、
   *  警告ではなく「切り上げが発生している」旨の注記表示にのみ使う。 */
  hasCeiledReferenceVideoDuration: boolean;
  imageGenerationModels: GenerationModel[];
  castingModelsQuery: CastingModelsQuery;
  DEFAULT_SCENARIO_MODEL: string;
  GENERATION_MODEL_SPEED: string;

  // 動画生成設定
  videoSettings: VideoSettings;
  setVideoSettings: React.Dispatch<React.SetStateAction<VideoSettings>>;
  autoGenerateNarration: boolean;
  setAutoGenerateNarration: React.Dispatch<React.SetStateAction<boolean>>;

  // キャスティング
  selectedCasting: SelectedCasting;
  setSelectedCasting: (casting: SelectedCasting) => void;
  additionalCastings: CastingActor[];
  setAdditionalCastings: React.Dispatch<React.SetStateAction<CastingActor[]>>;
  /** キャスト編集状態 (修正2-a)。CastingSection の各編集ハンドラが markCastingExplicit() で
   *  'explicit' へ遷移させる。 */
  castingSelectionState: CastingSelectionState;
  setCastingSelectionState: (state: CastingSelectionState) => void;
  markCastingExplicit: () => void;
  selectedSeedancePresetActor: SelectedSeedancePresetActor;
  setSelectedSeedancePresetActor: (actor: SelectedSeedancePresetActor) => void;
  additionalSeedancePresetActors: SeedancePresetActorItem[];
  setAdditionalSeedancePresetActors: React.Dispatch<React.SetStateAction<SeedancePresetActorItem[]>>;

  // 台本取り込み (docs/features/scenario-script-import-plan.md §4 / §7)。
  /** 読み込んだ台本 (解析 API の結果)。null = 通常の生成。ページ再読込で消える (永続化しない)。
   *  非 null の間はトーン・ナレーション/セリフ/テロップ・方言を「台本に従う」で非活性にし、シナリオ数を1案に固定する。 */
  importedScript: ImportedScriptState | null;
  setImportedScript: React.Dispatch<React.SetStateAction<ImportedScriptState | null>>;
  // 収録動画から (docs/features/video-import-motion-scenario-plan.md §3.2)。動画エディタから遷移の state で受け取った選択。
  /** null = 通常の生成。非 null なら生成 payload に source_video を付ける。ページ再読込で消える */
  sourceVideo: SourceVideoState | null;
  setSourceVideo: React.Dispatch<React.SetStateAction<SourceVideoState | null>>;
}

const ScenarioSetupContext = createContext<ScenarioSetupContextValue | null>(null);

export function ScenarioSetupProvider({
  value,
  children,
}: {
  value: ScenarioSetupContextValue;
  children: ReactNode;
}) {
  return <ScenarioSetupContext.Provider value={value}>{children}</ScenarioSetupContext.Provider>;
}

export function useScenarioSetup(): ScenarioSetupContextValue {
  const ctx = useContext(ScenarioSetupContext);
  if (!ctx) {
    throw new Error('useScenarioSetup must be used inside <ScenarioSetupProvider>');
  }
  return ctx;
}
